import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import GetAppIcon from '@mui/icons-material/GetApp';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs from 'dayjs';
import { DataGrid, SelectColumn } from 'react-data-grid';
import update from 'immutability-helper';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import _ from 'lodash';

import ExportExcel from '../../Components/ExportExcel';
import ImportExcel from '../../Components/ImportExcel';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  autoFocusAndSelect,
  handleCopy,
  renderTextEditor,
  mergeEditedGridRows,
} from '../../Components/ReactDataGridProps';
import ManageReportDataApi from '../../Api/ApiManageReportData';


function IncomeStatement({
  name,
  rows,
  companies,
  organisations,
  dates,
  activeFilter,
  chartofaccounts,
  selectedCompany,
  selectedDates,
  orgLength,
  onClickedAdd,
  onClickedDelete,
  getStateData,
  resetReportingState,
}) {
  const [tableRows, setTableRows] = useState([]);
  const [filters, setFilters] = useState({
    DimDateKey: 'All',
    DimChartOfAccountsKey: 'All',
    DimCompanyKey: 'All',
    DimOrganisationKey: 'All',
    IncomeStatementCategory: '',
    IncomeStatementSubCategory: '',
    Actual: '',
    Budget: '',
    Forecast: '',
    ActualVsBudgetVariance: '',
    ActualVsForecastVariance: '',
    YTDActual: '',
    YTDBudget: '',
    YTDForecast: '',
    YTDActualVsBudgetVariance: '',
    YTDActualVsForecastVariance: '',
    enabled: false,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortColumns, setSortColumns] = useState([]);
  const [frozenColumns, setFrozenColumns] = useState([]);
  const [open, setOpen] = useState(false);
  const [exportAllData, setExportAllData] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'IncomeStatementCategory':
      case 'IncomeStatementSubCategory':
        return (a, b) => {
          return a[sortColumn]?.localeCompare(b[sortColumn]);
        };
  
      case 'DimDateKey':
      case 'Actual':
      case 'Budget':
      case 'Forecast':
      case 'ActualVsBudgetVariance':
      case 'ActualVsForecastVariance':
      case 'YTDActual':
      case 'YTDBudget':
      case 'YTDForecast':
      case 'YTDActualVsBudgetVariance':
      case 'YTDActualVsForecastVariance':
        return (a, b) => {
          return a[sortColumn] - b[sortColumn];
        };

      case 'DimCompanyKey':
        return (a, b) => {
          const aName = getCompanyNameById(a[sortColumn]);
          const bName = getCompanyNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimOrganisationKey':
        return (a, b) => {
          const aName = getOrganisationNameById(a[sortColumn]);
          const bName = getOrganisationNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
  
      case 'DimChartOfAccountsKey':
        return (a, b) => {
          const aName = getChartOfAccountsNameById(a[sortColumn]);
          const bName = getChartOfAccountsNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
  
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }
  
  useEffect(() => {
    let startMonth = companies.find((com) => com.id === selectedCompany)
      ? companies.find((com) => com.id === selectedCompany).FiscalYearStartMonth
      : 7;
    let startDate, endDate;

    if (new Date().getMonth() >= startMonth - 1) {
      startDate = new Date().getFullYear() * 10000 + startMonth * 100 + 1;
      endDate =
        (new Date().getFullYear() + 1) * 10000 + (startMonth - 1) * 100 + 1;
    } else {
      startDate = (new Date().getFullYear() - 1) * 10000 + startMonth * 100 + 1;
      endDate = new Date().getFullYear() * 10000 + (startMonth - 1) * 100 + 1;
    }

    rows.map((data) => {
      data.DimAccountName = chartofaccounts.find(
        (acc) => acc[0] === data.DimChartOfAccountsKey,
      )
        ? chartofaccounts.find(
            (acc) => acc[0] === data.DimChartOfAccountsKey,
          )[1]
        : '';

      return data;
    });
    rows.sort((a, b) =>
      b.DimDateKey === a.DimDateKey
        ? a.DimAccountName < b.DimAccountName
          ? 1
          : -1
        : b.DimDateKey - a.DimDateKey,
    );

    let filterRows = [];
    if (activeFilter) {
      filterRows = rows.filter(
        (row) => row.DimDateKey >= startDate && row.DimDateKey <= endDate,
      );
    } else filterRows = rows;

    filterRows.sort((a, b) => b.DimDateKey - a.DimDateKey);

    setTableRows(filterRows);
  }, [rows, activeFilter, companies, selectedCompany, chartofaccounts]);

  const getCompanyNameById = (id) => {
    if (!id) return '';
    const company = companies?.find(company => company.id === id);
    return company ? company.CompanyName : '';
  };

  const getOrganisationNameById = (id) => {
    if (!id) return '';
    const organisation = organisations?.find(organisation => organisation.id === id);
    return organisation ? organisation.OrgName : '';
  };

  const getChartOfAccountsNameById = (id) => {
    if (!id) return '';
    const account = chartofaccounts?.find(account => account[0] === id);
    return account ? account[1] : '';
  };
  
  const handleClickRow = (ids) => {
    setSelectedRow(null);
    setSelectedIds(ids);
  };

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = [
      'DimCompanyKey',
      'DimOrganisationKey',
      'DimDateKey',
      'DimChartOfAccountsKey',
      'Budget',
      'Forecast',
      'ActualVsBudgetVariance',
      'ActualVsForecastVariance',
      'YTDBudget',
      'YTDForecast',
      'YTDActualVsBudgetVariance',
      'YTDActualVsForecastVariance',
    ];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleClickDelete = () => {
    if (selectedIds.size > 0) {
      onClickedDelete(name, Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectedRow(null);
    } else {
      if (selectedRow) {
        let rowIndexes = [selectedRow.id];
        onClickedDelete(name, rowIndexes);
        setSelectedRow(null);
      }
    }
  };

  const handleSelectFrozenColumns = (event, name) => {
    event.stopPropagation();
    event.preventDefault();
    const currentIndex = frozenColumns.indexOf(name);
    const newColumns = [...frozenColumns];

    if (currentIndex === -1) {
      newColumns.push(name);
    } else {
      newColumns.splice(currentIndex, 1);
    }
    setFrozenColumns(newColumns);
  };

  const handleExcelImport = (updated_data) => {
    // check import data is valid
    const validData = updated_data.every(item => {
      return item.hasOwnProperty('DimChartOfAccountsKey') && item.hasOwnProperty('IncomeStatementCategory')
        && item.hasOwnProperty('IncomeStatementSubCategory') && item.hasOwnProperty('Actual') && item.hasOwnProperty('Budget')
        && item.hasOwnProperty('Forecast') && item.hasOwnProperty('ActualVsBudgetVariance') && item.hasOwnProperty('ActualVsForecastVariance')
        && item.hasOwnProperty('YTDActual') && item.hasOwnProperty('YTDBudget') && item.hasOwnProperty('YTDForecast')
        && item.hasOwnProperty('YTDActualVsBudgetVariance') && item.hasOwnProperty('YTDActualVsForecastVariance');
    });
    if (!validData) {
      toast.warning('Please insert the valid data. Data must contain DimChartOfAccountsKey, IncomeStatementCategory, IncomeStatementSubCategory, Actual, Budget, Forecast, ActualVsBudgetVariance, ActualVsForecastVariance, YTDActual, YTDBudget, YTDForecast, YTDActualVsBudgetVariance, and YTDActualVsForecastVariance.');
      return;
    }

    ManageReportDataApi.importDataFromExcel(
      name,
      updated_data,
      selectedDates,
    ).then((res) => {
      if (res.status === 200) {
        getStateData(res.data);
      } else toast.error(res.message);
    })
    .catch((error) => toast.error(error.message));
  };

  const handleGridRowsUpdated = async (updatedRows, field) => {
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    if (updated_data.updated[field.column.key] === '') {
      toast.warning('Please insert the column value.');
      return;
    }

    if (
      field.column.key === 'Actual' ||
      field.column.key === 'Budget' ||
      field.column.key === 'Forecast' ||
      field.column.key === 'ActualVsBudgetVariance' ||
      field.column.key === 'ActualVsForecastVariance' ||
      field.column.key === 'YTDActual' ||
      field.column.key === 'YTDBudget' ||
      field.column.key === 'YTDForecast' ||
      field.column.key === 'YTDActualVsBudgetVariance' ||
      field.column.key === 'YTDActualVsForecastVariance'
    ) {
      if (isNaN(updated_data.updated[field.column.key])) {
        toast.warning('Please insert the number value.');
        return;
      }
    }

    if (selectedIds.size > 1) {
      updated_data.rowIds = [
        ...updated_data.rowIds,
        ...Array.from(selectedIds),
      ];
      updated_data.rowIds.forEach((idx) => {
        let updateFields = updated_data.updated;
        let rowIndex = _.findIndex(updatedRows, { id: idx });
        let updatedRow = updatedRows[rowIndex];
        let account = chartofaccounts.find((account) => account[0] === updatedRow.DimChartOfAccountsKey);
        let accountIncome = false;
        accountIncome = account ? account[7] : false;

        if (accountIncome) {
          if (field.column.key === 'Actual') {
            if (updatedRows[rowIndex]['Budget'] !== null) {
              updateFields['ActualVsBudgetVariance'] = parseInt(
                parseInt(updateFields[field.column.key]) - updatedRows[rowIndex]['Budget'],
              );
            }
            if (updatedRows[rowIndex]['Forecast'] !== null) {
              updateFields['ActualVsForecastVariance'] = parseInt(
                parseInt(updateFields[field.column.key]) - updatedRows[rowIndex]['Forecast'],
              );
            }
          } else if (field.column.key === 'Budget') {
            if (updatedRows[rowIndex]['Actual'] !== null) {
              updateFields['ActualVsBudgetVariance'] =
                parseInt(updatedRows[rowIndex]['Actual']) - parseInt(updateFields[field.column.key]);
            }
          } else if (field.column.key === 'Forecast') {
            if (updatedRows[rowIndex]['Actual'] !== null) {
              updateFields['ActualVsForecastVariance'] =
                parseInt(updatedRows[rowIndex]['Actual']) - parseInt(updateFields[field.column.key]);
            }
          }
  
          if (field.column.key === 'YTDActual') {
            if (updatedRows[rowIndex]['YTDBudget'] !== null) {
              updateFields['YTDActualVsBudgetVariance'] = parseInt(
                parseInt(updateFields[field.column.key]) - updatedRows[rowIndex]['YTDBudget'],
              );
            }
            if (updatedRows[rowIndex]['YTDForecast'] !== null) {
              updateFields['YTDActualVsForecastVariance'] = parseInt(
                parseInt(updateFields[field.column.key]) - updatedRows[rowIndex]['YTDForecast'],
              );
            }
          } else if (field.column.key === 'YTDBudget') {
            if (updatedRows[rowIndex]['YTDActual'] !== null) {
              updateFields['YTDActualVsBudgetVariance'] =
                parseInt(updatedRows[rowIndex]['YTDActual']) - parseInt(updateFields[field.column.key]);
            }
          } else if (field.column.key === 'YTDForecast') {
            if (updatedRows[rowIndex]['YTDActual'] !== null) {
              updateFields['YTDActualVsForecastVariance'] =
                parseInt(updatedRows[rowIndex]['YTDActual']) - parseInt(updateFields[field.column.key]);
            }
          }
        } else {
          if (field.column.key === 'Actual') {
            if (updatedRows[rowIndex]['Budget'] !== null) {
              updateFields['ActualVsBudgetVariance'] = parseInt(
                updatedRows[rowIndex]['Budget'] -
                  parseInt(updateFields[field.column.key]),
              );
            }
            if (updatedRows[rowIndex]['Forecast'] !== null) {
              updateFields['ActualVsForecastVariance'] = parseInt(
                updatedRows[rowIndex]['Forecast'] -
                  parseInt(updateFields[field.column.key]),
              );
            }
          } else if (field.column.key === 'Budget') {
            if (updatedRows[rowIndex]['Actual'] !== null) {
              updateFields['ActualVsBudgetVariance'] =
                parseInt(updateFields[field.column.key]) -
                parseInt(updatedRows[rowIndex]['Actual']);
            }
          } else if (field.column.key === 'Forecast') {
            if (updatedRows[rowIndex]['Actual'] !== null) {
              updateFields['ActualVsForecastVariance'] =
                parseInt(updateFields[field.column.key]) -
                parseInt(updatedRows[rowIndex]['Actual']);
            }
          }
  
          if (field.column.key === 'YTDActual') {
            if (updatedRows[rowIndex]['YTDBudget'] !== null) {
              updateFields['YTDActualVsBudgetVariance'] = parseInt(
                updatedRows[rowIndex]['YTDBudget'] -
                  parseInt(updateFields[field.column.key]),
              );
            }
            if (updatedRows[rowIndex]['YTDForecast'] !== null) {
              updateFields['YTDActualVsForecastVariance'] = parseInt(
                updatedRows[rowIndex]['YTDForecast'] -
                  parseInt(updateFields[field.column.key]),
              );
            }
          } else if (field.column.key === 'YTDBudget') {
            if (updatedRows[rowIndex]['YTDActual'] !== null) {
              updateFields['YTDActualVsBudgetVariance'] =
                parseInt(updateFields[field.column.key]) -
                parseInt(updatedRows[rowIndex]['YTDActual']);
            }
          } else if (field.column.key === 'YTDForecast') {
            if (updatedRows[rowIndex]['YTDActual'] !== null) {
              updateFields['YTDActualVsForecastVariance'] =
                parseInt(updateFields[field.column.key]) -
                parseInt(updatedRows[rowIndex]['YTDActual']);
            }
          }
        }

        updatedRows = update(updatedRows, {
          [rowIndex]: { $merge: updateFields },
        });
      });
    } else {
      let id = updated_data.rowIds[0];
      let rowIndex = _.findIndex(updatedRows, { id });
      let updatedRow = updatedRows[rowIndex];
      let updateFields = updated_data.updated;
      let fieldName = Object.keys(updateFields)[0];
      let account = chartofaccounts.find((account) => account[0] === updatedRow.DimChartOfAccountsKey);
      let accountIncome = false;
      accountIncome = account ? account[7] : false;
      
      if (accountIncome) {
        if (fieldName === 'Actual') {
          if (updatedRows[rowIndex]['Budget'] !== null) {
            updateFields['ActualVsBudgetVariance'] = parseInt(
              parseInt(updateFields[fieldName]) - updatedRows[rowIndex]['Budget'],
            );
          }
          if (updatedRows[rowIndex]['Forecast'] !== null) {
            updateFields['ActualVsForecastVariance'] = parseInt(
              parseInt(updateFields[fieldName]) - updatedRows[rowIndex]['Forecast'],
            );
          }
        } else if (fieldName === 'Budget') {
          if (updatedRows[rowIndex]['Actual'] !== null) {
            updateFields['ActualVsBudgetVariance'] =
              parseInt(updatedRows[rowIndex]['Actual']) - parseInt(updateFields[fieldName]);
          }
        } else if (fieldName === 'Forecast') {
          if (updatedRows[rowIndex]['Actual'] !== null) {
            updateFields['ActualVsForecastVariance'] =
              parseInt(updatedRows[rowIndex]['Actual']) - parseInt(updateFields[fieldName]);
          }
        }
  
        if (fieldName === 'YTDActual') {
          if (updatedRows[rowIndex]['YTDBudget'] !== null) {
            updateFields['YTDActualVsBudgetVariance'] = parseInt(
              parseInt(updateFields[fieldName]) - updatedRows[rowIndex]['YTDBudget'],
            );
          }
          if (updatedRows[rowIndex]['YTDForecast'] !== null) {
            updateFields['YTDActualVsForecastVariance'] = parseInt(
              parseInt(updateFields[fieldName]) - updatedRows[rowIndex]['YTDForecast'],
            );
          }
        } else if (fieldName === 'YTDBudget') {
          if (updatedRows[rowIndex]['YTDActual'] !== null) {
            updateFields['YTDActualVsBudgetVariance'] =
              parseInt(updatedRows[rowIndex]['YTDActual']) - parseInt(updateFields[fieldName]);
          }
        } else if (fieldName === 'YTDForecast') {
          if (updatedRows[rowIndex]['YTDActual'] !== null) {
            updateFields['YTDActualVsForecastVariance'] =
              parseInt(updatedRows[rowIndex]['YTDActual']) - parseInt(updateFields[fieldName]);
          }
        }
      } else {
        if (fieldName === 'Actual') {
          if (updatedRows[rowIndex]['Budget'] !== null) {
            updateFields['ActualVsBudgetVariance'] = parseInt(
              updatedRows[rowIndex]['Budget'] - parseInt(updateFields[fieldName]),
            );
          }
          if (updatedRows[rowIndex]['Forecast'] !== null) {
            updateFields['ActualVsForecastVariance'] = parseInt(
              updatedRows[rowIndex]['Forecast'] -
                parseInt(updateFields[fieldName]),
            );
          }
        } else if (fieldName === 'Budget') {
          if (updatedRows[rowIndex]['Actual'] !== null) {
            updateFields['ActualVsBudgetVariance'] =
              parseInt(updateFields[fieldName]) -
              parseInt(updatedRows[rowIndex]['Actual']);
          }
        } else if (fieldName === 'Forecast') {
          if (updatedRows[rowIndex]['Actual'] !== null) {
            updateFields['ActualVsForecastVariance'] =
              parseInt(updateFields[fieldName]) -
              parseInt(updatedRows[rowIndex]['Actual']);
          }
        }
  
        if (fieldName === 'YTDActual') {
          if (updatedRows[rowIndex]['YTDBudget'] !== null) {
            updateFields['YTDActualVsBudgetVariance'] = parseInt(
              updatedRows[rowIndex]['YTDBudget'] -
                parseInt(updateFields[fieldName]),
            );
          }
          if (updatedRows[rowIndex]['YTDForecast'] !== null) {
            updateFields['YTDActualVsForecastVariance'] = parseInt(
              updatedRows[rowIndex]['YTDForecast'] -
                parseInt(updateFields[fieldName]),
            );
          }
        } else if (fieldName === 'YTDBudget') {
          if (updatedRows[rowIndex]['YTDActual'] !== null) {
            updateFields['YTDActualVsBudgetVariance'] =
              parseInt(updateFields[fieldName]) -
              parseInt(updatedRows[rowIndex]['YTDActual']);
          }
        } else if (fieldName === 'YTDForecast') {
          if (updatedRows[rowIndex]['YTDActual'] !== null) {
            updateFields['YTDActualVsForecastVariance'] =
              parseInt(updateFields[fieldName]) -
              parseInt(updatedRows[rowIndex]['YTDActual']);
          }
        }
      }
      
      updatedRows = update(updatedRows, {
        [rowIndex]: { $merge: updateFields },
      });
    }

    setTableRows((currentRows) =>
      mergeEditedGridRows(
        currentRows,
        updatedRows,
        updated_data.rowIds,
        updated_data.updated,
      ),
    );
    ManageReportDataApi.updateTemplate(name, updated_data)
      .then((res) => {
        if (res.status === 500) {
          toast.error(res.message);
        } else {
          resetReportingState();
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const handleDateChange = (date, field) => {
    if (field === 'StartDate') {
      if (endDate) {
        if (
          new Date(date.$d).getTime() - 3600000 >
          new Date(endDate).getTime()
        ) {
          toast.warning(
            'Start date must be less than the end date!',
          );
          return;
        } else {
          setStartDate(date.$d);
        }
      } else setStartDate(date.$d);
    } else {
      if (startDate) {
        if (new Date(startDate).getTime() > new Date(date.$d).getTime()) {
          toast.warning(
            'End date must be greater than the start date!',
          );
          return;
        } else {
          setEndDate(date.$d);
        }
      } else setEndDate(date.$d);
    }
  };

  const handleExcelExport = (exportData) => {
    if (exportAllData) {
      if (exportData.length === 0)
        toast.warning('There is no export data!');
      setOpen(false);
    } else {
      if (startDate && endDate) {
        if (exportData.length === 0)
          toast.warning('There is no export data!');
        setOpen(false);
      } else {
        toast.warning(
          'Please select the start date and end date!',
        );
        return;
      }
    }
  };

  const plChartOfAccounts = useMemo(() => {
    return chartofaccounts.filter((account) => account[6] === 'P&L');
  }, [chartofaccounts]);
  
  const incomeStatementColumns = useMemo(() => {
    let income_statement_columns = [
      SelectColumn,
      {
        key: 'DimDateKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Period</b>
            {frozenColumns.indexOf('DimDateKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimDateKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimDateKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimDateKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'DimDateKey')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        frozen: frozenColumns.indexOf('DimDateKey') !== -1 ? true : false,
        width: 150,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimDateKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimDateKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {dates &&
                  dates.map((cur) => (
                    <option key={cur[0]} value={cur[0]}>
                      {cur[2] + ' ' + cur[3]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimDateKey ? props.row.DimDateKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimDateKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {dates &&
                dates.map((cur) => (
                  <MenuItem key={cur[0]} value={cur[0]}>
                    {cur[2] + ' ' + cur[3]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimChartOfAccountsKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Account</b>
            {frozenColumns.indexOf('DimChartOfAccountsKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimChartOfAccountsKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimChartOfAccountsKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimChartOfAccountsKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimChartOfAccountsKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        frozen:
          frozenColumns.indexOf('DimChartOfAccountsKey') !== -1 ? true : false,
        resizable: true,
        sortable: true,
        width: 150,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimChartOfAccountsKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimChartOfAccountsKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {plChartOfAccounts &&
                  plChartOfAccounts.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'block' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimChartOfAccountsKey
                  ? props.row.DimChartOfAccountsKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimChartOfAccountsKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {plChartOfAccounts &&
                plChartOfAccounts.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'block' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'IncomeStatementCategory',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Category</b>
            {frozenColumns.indexOf('IncomeStatementCategory') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'IncomeStatementCategory')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'IncomeStatementCategory')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'IncomeStatementCategory',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'IncomeStatementCategory',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        width: 175,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.IncomeStatementCategory}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    IncomeStatementCategory: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('IncomeStatementCategory') !== -1
            ? true
            : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'IncomeStatementSubCategory',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Sub Category</b>
            {frozenColumns.indexOf('IncomeStatementSubCategory') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'IncomeStatementSubCategory')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'IncomeStatementSubCategory')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'IncomeStatementSubCategory',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'IncomeStatementSubCategory',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        width: 175,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.IncomeStatementSubCategory}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    IncomeStatementSubCategory: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('IncomeStatementSubCategory') !== -1
            ? true
            : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'Actual',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Actual</b>
            {frozenColumns.indexOf('Actual') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Actual')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Actual')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'Actual')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Actual')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 100,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Actual}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Actual: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('Actual') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.Actual}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                Actual: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'Budget',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Budget</b>
            {frozenColumns.indexOf('Budget') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Budget')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Budget')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'Budget')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Budget')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 105,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Budget}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Budget: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('Budget') !== -1 ? true : false,
      },
      {
        key: 'Forecast',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Forecast</b>
            {frozenColumns.indexOf('Forecast') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Forecast')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Forecast')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'Forecast')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Forecast')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 115,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Forecast}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Forecast: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('Forecast') !== -1 ? true : false,
      },
      {
        key: 'ActualVsBudgetVariance',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Budget Variance</b>
            {frozenColumns.indexOf('ActualVsBudgetVariance') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'ActualVsBudgetVariance')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'ActualVsBudgetVariance')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'ActualVsBudgetVariance',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'ActualVsBudgetVariance',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 180,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ActualVsBudgetVariance}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ActualVsBudgetVariance: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('ActualVsBudgetVariance') !== -1 ? true : false,
      },
      {
        key: 'ActualVsForecastVariance',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Forecast Variance</b>
            {frozenColumns.indexOf('ActualVsForecastVariance') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'ActualVsForecastVariance')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'ActualVsForecastVariance')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'ActualVsForecastVariance',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'ActualVsForecastVariance',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 190,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ActualVsForecastVariance}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ActualVsForecastVariance: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('ActualVsForecastVariance') !== -1
            ? true
            : false,
      },
      {
        key: 'YTDActual',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>YTD Actual</b>
            {frozenColumns.indexOf('YTDActual') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'YTDActual')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'YTDActual')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'YTDActual')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'YTDActual')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 135,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.YTDActual}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    YTDActual: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('YTDActual') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.YTDActual}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                YTDActual: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'YTDBudget',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>YTD Budget</b>
            {frozenColumns.indexOf('YTDBudget') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'YTDBudget')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'YTDBudget')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'YTDBudget')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'YTDBudget')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 140,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.YTDBudget}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    YTDBudget: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('YTDBudget') !== -1 ? true : false,
      },
      {
        key: 'YTDForecast',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>YTD Forecast</b>
            {frozenColumns.indexOf('YTDForecast') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'YTDForecast')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'YTDForecast')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'YTDForecast')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'YTDForecast')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 150,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.YTDForecast}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    YTDForecast: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('YTDForecast') !== -1 ? true : false,
      },
      {
        key: 'YTDActualVsBudgetVariance',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>YTD Budget Variance</b>
            {frozenColumns.indexOf('YTDActualVsBudgetVariance') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'YTDActualVsBudgetVariance')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'YTDActualVsBudgetVariance')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'YTDActualVsBudgetVariance',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'YTDActualVsBudgetVariance',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 215,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.YTDActualVsBudgetVariance}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    YTDActualVsBudgetVariance: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('YTDActualVsBudgetVariance') !== -1
            ? true
            : false,
      },
      {
        key: 'YTDActualVsForecastVariance',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>YTD Forecast Variance</b>
            {frozenColumns.indexOf('YTDActualVsForecastVariance') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'YTDActualVsForecastVariance')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'YTDActualVsForecastVariance')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'YTDActualVsForecastVariance',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'YTDActualVsForecastVariance',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 225,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.YTDActualVsForecastVariance}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    YTDActualVsForecastVariance: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('YTDActualVsForecastVariance') !== -1
            ? true
            : false,
      },
    ];

    if (orgLength.length > 1) {
      income_statement_columns.splice(1, 0, {
        key: 'DimOrganisationKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Department</b>
            {frozenColumns.indexOf('DimOrganisationKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimOrganisationKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimOrganisationKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimOrganisationKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimOrganisationKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimOrganisationKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {organisations &&
                  organisations.map(
                    (cur) =>
                      p.row.DimCompanyKey === cur.DimCompanyKey &&
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.OrgName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimOrganisationKey != null &&
                props.row.DimOrganisationKey
              }
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimOrganisationKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {organisations &&
                organisations.map(
                  (cur) =>
                    props.row.DimCompanyKey === cur.DimCompanyKey &&
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.OrgName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    if (companies.length > 1) {
      income_statement_columns.splice(1, 0, {
        key: 'DimCompanyKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Company</b>
            {frozenColumns.indexOf('DimCompanyKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimCompanyKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimCompanyKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('DimCompanyKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimCompanyKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimCompanyKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {companies &&
                  companies.map(
                    (cur) =>
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.CompanyName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimCompanyKey != null && props.row.DimCompanyKey}
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimCompanyKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {companies &&
                companies.map(
                  (cur) =>
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.CompanyName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    let frozenCols = income_statement_columns.filter(
      (col) => col.frozen === true,
    );
    let unfrozenCols = income_statement_columns.filter(
      (col) => col.frozen === false,
    );
    income_statement_columns = [...frozenCols, ...unfrozenCols];

    return income_statement_columns;
    // eslint-disable-next-line
  }, [
    companies,
    organisations,
    tableRows,
    sortColumns,
    frozenColumns,
    plChartOfAccounts,
    dates,
    handleSelectFrozenColumns,
  ]);

  const filteredRows = useMemo(() => {
    return tableRows?.filter((r) => {
      return (
        (filters.DimDateKey !== 'All'
          ? // eslint-disable-next-line
            r.DimDateKey == filters.DimDateKey
          : true) &&
        (filters.DimChartOfAccountsKey !== 'All'
          ? // eslint-disable-next-line
            r.DimChartOfAccountsKey == filters.DimChartOfAccountsKey
          : true) &&
        (filters.IncomeStatementCategory
          ? r.IncomeStatementCategory?.toLowerCase().includes(
              filters.IncomeStatementCategory.toLowerCase(),
            )
          : true) &&
        (filters.IncomeStatementSubCategory
          ? r.IncomeStatementSubCategory?.toLowerCase().includes(
              filters.IncomeStatementSubCategory.toLowerCase(),
            )
          : true) &&
        (filters.Actual
          ? r.Actual?.toString().includes(filters.Actual.toString())
          : true) &&
        (filters.Budget
          ? r.Budget?.toString().includes(filters.Budget.toString())
          : true) &&
        (filters.Forecast
          ? r.Forecast?.toString().includes(filters.Forecast.toString())
          : true) &&
        (filters.ActualVsBudgetVariance
          ? r.ActualVsBudgetVariance?.toString().includes(
              filters.ActualVsBudgetVariance.toString(),
            )
          : true) &&
        (filters.ActualVsForecastVariance
          ? r.ActualVsForecastVariance?.toString().includes(
              filters.ActualVsForecastVariance.toString(),
            )
          : true) &&
        (filters.YTDActual
          ? r.YTDActual?.toString().includes(filters.YTDActual.toString())
          : true) &&
        (filters.YTDBudget
          ? r.YTDBudget?.toString().includes(filters.YTDBudget.toString())
          : true) &&
        (filters.YTDForecast
          ? r.YTDForecast?.toString().includes(filters.YTDForecast.toString())
          : true) &&
        (filters.YTDActualVsBudgetVariance
          ? r.YTDActualVsBudgetVariance?.toString().includes(
              filters.YTDActualVsBudgetVariance.toString(),
            )
          : true) &&
        (filters.YTDActualVsForecastVariance
          ? r.YTDActualVsForecastVariance?.toString().includes(
              filters.YTDActualVsForecastVariance.toString(),
            )
          : true) &&
        (filters.DimCompanyKey !== 'All'
          ? r.DimCompanyKey === filters.DimCompanyKey
          : true) &&
        (filters.DimOrganisationKey !== 'All'
          ? r.DimOrganisationKey === filters.DimOrganisationKey
          : true)
      );
    });
  }, [tableRows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  const exportData = useMemo(() => {
    let export_data = [];

    if (exportAllData) {
      export_data = [...tableRows];
    } else {
      if (startDate && endDate) {
        let start_date =
          new Date(startDate).getFullYear() * 10000 +
          (new Date(startDate).getMonth() + 1) * 100 +
          1;
        let end_date =
          new Date(endDate).getFullYear() * 10000 +
          (new Date(endDate).getMonth() + 1) * 100 +
          1;
        export_data = [...tableRows];
        export_data = export_data.filter(
          (data) =>
            data.DimDateKey >= start_date && data.DimDateKey <= end_date,
        );
      }
    }

    return export_data;
  }, [exportAllData, tableRows, startDate, endDate]);

  const exportValue = useMemo(() => {
    let export_value = {
      'Income Statement': {
        columns: [],
        data: [],
      },
      Company: {
        columns: [],
        data: [],
      },
      Organisation: {
        columns: [],
        data: [],
      },
      Date: {
        columns: [],
        data: [],
      },
      'General Ledger': {
        columns: [],
        data: [],
      },
    };

    export_value['Income Statement'].columns = [
      { title: 'DimCompanyKey', width: { wpx: 80 } },
      { title: 'DimOrganisationKey', width: { wpx: 80 } },
      { title: 'DimDateKey', width: { wpx: 80 } },
      { title: 'DimAccountName', width: { wpx: 80 } },
      { title: 'IncomeStatementCategory', width: { wpx: 80 } },
      { title: 'IncomeStatementSubCategory', width: { wpx: 80 } },
      { title: 'Actual', width: { wpx: 80 } },
      { title: 'Budget', width: { wpx: 80 } },
      { title: 'Forecast', width: { wpx: 80 } },
      { title: 'ActualVsBudgetVariance', width: { wpx: 80 } },
      { title: 'ActualVsForecastVariance', width: { wpx: 80 } },
      { title: 'YTDActual', width: { wpx: 80 } },
      { title: 'YTDBudget', width: { wpx: 80 } },
      { title: 'YTDForecast', width: { wpx: 80 } },
      { title: 'YTDActualVsBudgetVariance', width: { wpx: 80 } },
      { title: 'YTDActualVsForecastVariance', width: { wpx: 80 } },
    ];
    exportData.forEach((data) => {
      export_value['Income Statement'].data.push([
        {
          value: data.DimCompanyKey ? Number(data.DimCompanyKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimOrganisationKey ? Number(data.DimOrganisationKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimDateKey ? Number(data.DimDateKey) : -1,
          style: { numFmt: '0' },
        },
        { value: data.DimAccountName ? data.DimAccountName : '' },
        {
          value: data.IncomeStatementCategory
            ? data.IncomeStatementCategory
            : '',
        },
        {
          value: data.IncomeStatementSubCategory
            ? data.IncomeStatementSubCategory
            : '',
        },
        {
          value: data.Actual ? Number(data.Actual) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.Budget ? Number(data.Budget) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.Forecast ? Number(data.Forecast) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.ActualVsBudgetVariance
            ? Number(data.ActualVsBudgetVariance)
            : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.ActualVsForecastVariance
            ? Number(data.ActualVsForecastVariance)
            : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.YTDActual ? Number(data.YTDActual) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.YTDBudget ? Number(data.YTDBudget) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.YTDForecast ? Number(data.YTDForecast) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.YTDActualVsBudgetVariance
            ? Number(data.YTDActualVsBudgetVariance)
            : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.YTDActualVsForecastVariance
            ? Number(data.YTDActualVsForecastVariance)
            : 0,
          style: { numFmt: '0.00' },
        },
      ]);
    });

    export_value.Company.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'CompanyName', width: { wpx: 80 } },
    ];
    companies &&
      companies.forEach((cur) => {
        export_value.Company.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.CompanyName },
        ]);
      });

    export_value.Organisation.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'OrgName', width: { wpx: 80 } },
    ];
    organisations &&
      organisations.forEach((cur) => {
        export_value.Organisation.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.OrgName },
        ]);
      });

    export_value.Date.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Date', width: { wpx: 80 } },
    ];
    dates &&
      dates.forEach((cur) => {
        export_value.Date.data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[2] + ' ' + cur[3] },
        ]);
      });

    export_value['General Ledger'].columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];
    dates &&
      dates.forEach((cur) => {
        export_value['General Ledger'].data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });

    return export_value;
  }, [exportData, companies, organisations, dates]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        <ImportExcel onImport={handleExcelImport} />
        <IconButton onClick={() => setOpen(true)}>
          <GetAppIcon />
        </IconButton>
        <IconButton
          onClick={() =>
            selectedDates.length === 1 ? onClickedAdd(name) : null
          }
        >
          <AddIcon />
        </IconButton>
        <IconButton onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          headerRowHeight={filters.enabled ? 70 : 50}
          columns={incomeStatementColumns}
          rows={sortedRows}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          selectedRows={selectedIds}
          onSelectedRowsChange={handleClickRow}
          onRowsChange={handleGridRowsUpdated}
          onCellClick={handleCellClick}
          onCopy={handleCopy}
          onPaste={handlePaste}
          rowHeight={50}
          enableVirtualization={true}
          style={{ maxHeight: window.innerHeight - 180 - (23 * (selectedDates.length - 1)) }}
        />
      </FilterContext.Provider>

      <Dialog
        open={open}
        maxWidth="sm"
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Select Date Range'}</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={exportAllData}
                onChange={(event) => setExportAllData(event.target.checked)}
                name="exportAllData"
                color="success"
              />
            }
            label="Export all data"
          />
          {!exportAllData ? (
            <Grid container spacing={1}>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="Start Date"
                      variant="inline"
                      value={dayjs(startDate)}
                      onChange={(event) => handleDateChange(event, 'StartDate')}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="End Date"
                      variant="inline"
                      value={dayjs(endDate)}
                      onChange={(event) => handleDateChange(event, 'EndDate')}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            color="error"
            size="small"
          >
            Cancel
          </Button>
          {exportData.length > 0 ? (
            <div onClick={() => setOpen(false)}>
              <ExportExcel filename="Income Statement" value={exportValue} />
            </div>
          ) : (
            <Button
              onClick={() => handleExcelExport(exportData)}
              variant="outlined"
              color="success"
              size="small"
            >
              Export
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}

IncomeStatement.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  companies: PropTypes.array.isRequired,
  organisations: PropTypes.array.isRequired,
  dates: PropTypes.array.isRequired,
  activeFilter: PropTypes.bool.isRequired,
  chartofaccounts: PropTypes.array.isRequired,
  selectedDates: PropTypes.array.isRequired,
  orgLength: PropTypes.array.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
};

export default IncomeStatement;
